import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/legal';

// Minimal SVG visuals for each system element
const TransparenzVisual = () => (
  <svg viewBox="0 0 280 200" className="w-full h-auto" fill="none">
    {/* Dashboard frame */}
    <rect x="20" y="20" width="240" height="160" rx="8" stroke="rgba(41, 163, 102, 0.3)" strokeWidth="1" />
    
    {/* Top bar */}
    <rect x="30" y="30" width="220" height="24" rx="4" fill="rgba(255,255,255,0.03)" />
    <circle cx="44" cy="42" r="4" fill="rgba(41, 163, 102, 0.5)" />
    <rect x="56" y="39" width="40" height="6" rx="2" fill="rgba(255,255,255,0.1)" />
    
    {/* Main chart area */}
    <rect x="30" y="64" width="140" height="80" rx="4" fill="rgba(255,255,255,0.02)" />
    {/* Chart line */}
    <path d="M 40 124 Q 60 100 80 110 T 120 90 T 160 70" stroke="rgba(41, 163, 102, 0.6)" strokeWidth="2" fill="none" />
    <circle cx="160" cy="70" r="4" fill="rgba(41, 163, 102, 0.8)" />
    
    {/* Side cards */}
    <rect x="180" y="64" width="70" height="36" rx="4" fill="rgba(255,255,255,0.03)" />
    <rect x="188" y="72" width="24" height="4" rx="1" fill="rgba(255,255,255,0.15)" />
    <rect x="188" y="82" width="40" height="8" rx="2" fill="rgba(41, 163, 102, 0.4)" />
    
    <rect x="180" y="108" width="70" height="36" rx="4" fill="rgba(255,255,255,0.03)" />
    <rect x="188" y="116" width="24" height="4" rx="1" fill="rgba(255,255,255,0.15)" />
    <rect x="188" y="126" width="32" height="8" rx="2" fill="rgba(75, 123, 245, 0.4)" />
    
    {/* Bottom stats */}
    <rect x="30" y="152" width="60" height="20" rx="3" fill="rgba(255,255,255,0.02)" />
    <rect x="100" y="152" width="60" height="20" rx="3" fill="rgba(255,255,255,0.02)" />
  </svg>
);

const StrukturVisual = () => (
  <svg viewBox="0 0 280 200" className="w-full h-auto" fill="none">
    {/* Folder stack */}
    <rect x="60" y="60" width="160" height="120" rx="6" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
    <rect x="70" y="50" width="140" height="10" rx="3" fill="rgba(255,255,255,0.05)" />
    
    {/* Document lines */}
    <rect x="80" y="80" width="80" height="6" rx="2" fill="rgba(255,255,255,0.1)" />
    <rect x="80" y="94" width="120" height="4" rx="1" fill="rgba(255,255,255,0.06)" />
    <rect x="80" y="104" width="100" height="4" rx="1" fill="rgba(255,255,255,0.06)" />
    <rect x="80" y="114" width="110" height="4" rx="1" fill="rgba(255,255,255,0.06)" />
    
    {/* Checkmarks */}
    <circle cx="190" cy="83" r="8" fill="rgba(41, 163, 102, 0.2)" />
    <path d="M 186 83 L 189 86 L 195 80" stroke="rgba(41, 163, 102, 0.8)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    
    {/* Lock icon */}
    <rect x="80" y="140" width="24" height="16" rx="3" stroke="rgba(41, 163, 102, 0.4)" strokeWidth="1" fill="none" />
    <path d="M 86 140 V 134 A 6 6 0 0 1 98 134 V 140" stroke="rgba(41, 163, 102, 0.4)" strokeWidth="1" fill="none" />
    <circle cx="92" cy="148" r="2" fill="rgba(41, 163, 102, 0.6)" />
    
    {/* Cloud sync indicator */}
    <path d="M 170 150 Q 165 142 172 138 Q 180 135 188 140 Q 195 138 198 145 Q 202 152 195 155 H 175 Q 168 155 170 150" stroke="rgba(75, 123, 245, 0.4)" strokeWidth="1" fill="rgba(75, 123, 245, 0.1)" />
  </svg>
);

const EffizienzVisual = () => (
  <svg viewBox="0 0 280 200" className="w-full h-auto" fill="none">
    {/* Process flow */}
    <circle cx="60" cy="100" r="24" stroke="rgba(41, 163, 102, 0.4)" strokeWidth="1" fill="rgba(41, 163, 102, 0.1)" />
    <text x="60" y="105" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10" fontFamily="monospace">01</text>
    
    <line x1="84" y1="100" x2="116" y2="100" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 2" />
    <polygon points="116,96 124,100 116,104" fill="rgba(255,255,255,0.15)" />
    
    <circle cx="140" cy="100" r="24" stroke="rgba(75, 123, 245, 0.4)" strokeWidth="1" fill="rgba(75, 123, 245, 0.1)" />
    <text x="140" y="105" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10" fontFamily="monospace">02</text>
    
    <line x1="164" y1="100" x2="196" y2="100" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 2" />
    <polygon points="196,96 204,100 196,104" fill="rgba(255,255,255,0.15)" />
    
    <circle cx="220" cy="100" r="24" stroke="rgba(41, 163, 102, 0.6)" strokeWidth="2" fill="rgba(41, 163, 102, 0.15)" />
    <text x="220" y="105" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10" fontFamily="monospace">03</text>
    
    {/* Labels */}
    <text x="60" y="140" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8">Daten</text>
    <text x="140" y="140" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8">Analyse</text>
    <text x="220" y="140" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8">Ergebnis</text>
    
    {/* Time saved indicator */}
    <rect x="90" y="160" width="100" height="20" rx="10" fill="rgba(41, 163, 102, 0.1)" stroke="rgba(41, 163, 102, 0.3)" strokeWidth="1" />
    <text x="140" y="174" textAnchor="middle" fill="rgba(41, 163, 102, 0.7)" fontSize="9">Automatisiert</text>
  </svg>
);

const UmsetzungVisual = () => (
  <svg viewBox="0 0 280 200" className="w-full h-auto" fill="none">
    {/* Central node */}
    <circle cx="140" cy="100" r="28" stroke="rgba(41, 163, 102, 0.5)" strokeWidth="2" fill="rgba(41, 163, 102, 0.1)" />
    <circle cx="140" cy="100" r="12" fill="rgba(41, 163, 102, 0.3)" />
    
    {/* Satellite nodes */}
    <circle cx="60" cy="60" r="18" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="rgba(255,255,255,0.03)" />
    <circle cx="220" cy="60" r="18" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="rgba(255,255,255,0.03)" />
    <circle cx="60" cy="140" r="18" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="rgba(255,255,255,0.03)" />
    <circle cx="220" cy="140" r="18" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="rgba(255,255,255,0.03)" />
    
    {/* Connection lines */}
    <line x1="78" y1="72" x2="118" y2="88" stroke="rgba(41, 163, 102, 0.3)" strokeWidth="1" />
    <line x1="202" y1="72" x2="162" y2="88" stroke="rgba(41, 163, 102, 0.3)" strokeWidth="1" />
    <line x1="78" y1="128" x2="118" y2="112" stroke="rgba(41, 163, 102, 0.3)" strokeWidth="1" />
    <line x1="202" y1="128" x2="162" y2="112" stroke="rgba(41, 163, 102, 0.3)" strokeWidth="1" />
    
    {/* Pulse rings */}
    <circle cx="140" cy="100" r="40" stroke="rgba(41, 163, 102, 0.15)" strokeWidth="1" fill="none" />
    <circle cx="140" cy="100" r="55" stroke="rgba(41, 163, 102, 0.08)" strokeWidth="1" fill="none" />
    
    {/* Labels on nodes */}
    <text x="60" y="64" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8">Partner</text>
    <text x="220" y="64" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8">Spezialist</text>
    <text x="60" y="144" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8">Experte</text>
    <text x="220" y="144" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8">Berater</text>
  </svg>
);

const Zusammenarbeit = () => {
  const navigate = useNavigate();

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  const fadeInLeft = {
    initial: { opacity: 0, x: -30 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  const fadeInRight = {
    initial: { opacity: 0, x: 30 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  return (
    <div className="min-h-screen bg-[#0c1017] font-manrope">
      <Navigation variant="dark" />

      {/* HERO */}
      <section className="pt-28 sm:pt-36 md:pt-44 pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-emerald/70 text-xs font-medium uppercase tracking-[0.25em] mb-4">
              System
            </p>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
              Zusammenarbeit
            </h1>
            <p className="text-xl sm:text-2xl text-white/50 leading-relaxed max-w-2xl mx-auto">
              Wie aus Strategie Struktur wird
            </p>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-8">
            <p className="text-lg sm:text-xl text-white/70 leading-relaxed">
              Eine gute Strategie zeigt sich nicht auf dem Papier.
            </p>
            <p className="text-base sm:text-lg text-white/50 leading-[1.8]">
              Sondern darin, wie sie im Alltag funktioniert.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-16 sm:py-24">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-6 text-base sm:text-lg text-white/50 leading-[1.8]">
            <p className="text-white/70">
              Viele finanzielle Entscheidungen scheitern nicht an der Idee, 
              sondern an der Umsetzung.
            </p>
            <p>
              Es wird geplant, optimiert und entschieden – 
              aber im Alltag fehlt oft die Struktur, um diese Entscheidungen sauber umzusetzen.
            </p>
            <p className="text-white/70 pt-4">
              Genau hier setzt meine Arbeit an.
            </p>
          </motion.div>
        </div>
      </section>

      {/* APPROACH STATEMENT */}
      <section className="py-20 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="text-center">
            <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-4">
              Ich begleite nicht nur die strategische Ausrichtung,
            </p>
            <p className="font-playfair text-2xl sm:text-3xl text-white/90 leading-relaxed">
              sondern sorge dafür, dass daraus eine funktionierende Struktur entsteht.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SYSTEM FLOW INDICATOR */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="flex items-center justify-center gap-4 sm:gap-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/30">Das System</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
          </motion.div>
        </div>
      </section>

      {/* SYSTEM ELEMENT 1 - Transparenz (Text Left, Visual Right) */}
      <section className="py-20 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInLeft} className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-emerald/10 flex items-center justify-center">
                  <span className="text-emerald/70 text-xs font-mono">01</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-emerald/20 to-transparent" />
              </div>
              <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-6">
                Transparenz
              </h2>
              <div className="space-y-5 text-base text-white/50 leading-[1.8]">
                <p className="text-white/70">
                  Sie wissen jederzeit, wo Sie stehen.
                </p>
                <p>
                  Alle Vermögenswerte, Verträge und Entwicklungen sind zentral gebündelt 
                  und nachvollziehbar aufbereitet.
                </p>
                <p>
                  Eine klare Übersicht ersetzt dabei das Gefühl, einzelne Entscheidungen 
                  nicht mehr vollständig im Blick zu haben.
                </p>
              </div>
            </motion.div>
            <motion.div {...fadeInRight} className="order-1 lg:order-2">
              {/* Planora Card */}
              <a 
                href="https://planora.finance"
                target="_blank"
                rel="noopener noreferrer"
                className="group block relative bg-gradient-to-br from-[#1a2235] to-[#0f1520] rounded-2xl p-6 sm:p-8 border border-white/10 hover:border-cobalt/30 transition-all duration-300 overflow-hidden"
                data-testid="planora-card-link"
              >
                {/* Background glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cobalt/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative">
                  {/* Planora Logo/Brand */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gradient-to-br from-cobalt to-cobalt/80 rounded-xl flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 16l4-4 4 4 6-6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <span className="text-white font-semibold text-lg">Planora</span>
                      <span className="text-white/40 text-sm ml-2">Vermögenstracker</span>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-white/60 text-sm leading-relaxed mb-6">
                    Ihr gesamtes Vermögen auf einen Blick – Konten, Depots, Immobilien und 
                    Versicherungen zentral verwaltet und übersichtlich dargestellt.
                  </p>
                  
                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50">Vermögensübersicht</span>
                    <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50">Echtzeit-Daten</span>
                    <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50">Sicher</span>
                  </div>
                  
                  {/* CTA */}
                  <div className="flex items-center gap-2 text-cobalt group-hover:text-cobalt/80 transition-colors">
                    <span className="text-sm font-medium">Mehr erfahren</span>
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Connection Line */}
      <div className="max-w-6xl mx-auto px-6 md:px-8 hidden lg:block">
        <div className="flex justify-center">
          <div className="w-px h-24 bg-gradient-to-b from-emerald/20 via-white/10 to-emerald/20" />
        </div>
      </div>

      {/* SYSTEM ELEMENT 2 - Struktur (Visual Left, Text Right) */}
      <section className="py-20 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInLeft} className="order-1">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_wealth-architect-12/artifacts/oc53vld1_simplr-verwaltung.avif"
                  alt="Simplr Verwaltung - Digitale Dokumentenstruktur"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
            <motion.div {...fadeInRight} className="order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <span className="text-blue-400/70 text-xs font-mono">02</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-blue-500/20 to-transparent" />
              </div>
              <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-6">
                Struktur
              </h2>
              <div className="space-y-5 text-base text-white/50 leading-[1.8]">
                <p className="text-white/70">
                  Keine verstreuten Unterlagen, kein Papierchaos.
                </p>
                <p>
                  Alle relevanten Dokumente sind digital, sicher und jederzeit zugänglich.
                </p>
                <p>
                  So entsteht eine klare Grundlage für Entscheidungen – 
                  ohne Suchen, ohne Unsicherheit.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Connection Line */}
      <div className="max-w-6xl mx-auto px-6 md:px-8 hidden lg:block">
        <div className="flex justify-center">
          <div className="w-px h-24 bg-gradient-to-b from-blue-500/20 via-white/10 to-emerald/20" />
        </div>
      </div>

      {/* SYSTEM ELEMENT 3 - Effizienz (Text Left, Visual Right) */}
      <section className="py-20 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInLeft} className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-emerald/10 flex items-center justify-center">
                  <span className="text-emerald/70 text-xs font-mono">03</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-emerald/20 to-transparent" />
              </div>
              <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-6">
                Effizienz
              </h2>
              <div className="space-y-5 text-base text-white/50 leading-[1.8]">
                <p>
                  Auch angrenzende Themen wie Steuern lassen sich sinnvoll und einfach 
                  in die Gesamtstrategie integrieren.
                </p>
                <p>
                  Digitale Lösungen helfen dabei, Abläufe zu vereinfachen 
                  und Zeitaufwand zu reduzieren.
                </p>
              </div>
            </motion.div>
            <motion.div {...fadeInRight} className="order-1 lg:order-2">
              {/* Taxfix Card */}
              <a 
                href="https://taxfix.de/ff-nico-helfrich/"
                target="_blank"
                rel="noopener noreferrer"
                className="group block relative bg-gradient-to-br from-[#1a2235] to-[#0f1520] rounded-2xl p-6 sm:p-8 border border-white/10 hover:border-emerald/30 transition-all duration-300 overflow-hidden"
                data-testid="taxfix-card-link"
              >
                {/* Background glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative">
                  {/* Taxfix Logo/Brand */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-[#00D084] rounded-xl flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                      </svg>
                    </div>
                    <div>
                      <span className="text-white font-semibold text-lg">Taxfix</span>
                      <span className="text-white/40 text-sm ml-2">Partner</span>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-white/60 text-sm leading-relaxed mb-6">
                    Steuererklärung einfach per App – schnell, sicher und ohne Vorwissen. 
                    Im Durchschnitt über 1.000 € Erstattung.
                  </p>
                  
                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50">Steuer-App</span>
                    <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50">Automatisiert</span>
                    <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50">TÜV-geprüft</span>
                  </div>
                  
                  {/* CTA */}
                  <div className="flex items-center gap-2 text-emerald group-hover:text-emerald/80 transition-colors">
                    <span className="text-sm font-medium">Mehr erfahren</span>
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Connection Line */}
      <div className="max-w-6xl mx-auto px-6 md:px-8 hidden lg:block">
        <div className="flex justify-center">
          <div className="w-px h-24 bg-gradient-to-b from-emerald/20 via-white/10 to-emerald/20" />
        </div>
      </div>

      {/* SYSTEM ELEMENT 4 - Umsetzung (Visual Left, Text Right) */}
      <section className="py-20 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInLeft} className="order-1">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-bl from-emerald/5 to-transparent rounded-2xl" />
                <div className="relative p-8 sm:p-12">
                  <UmsetzungVisual />
                </div>
              </div>
            </motion.div>
            <motion.div {...fadeInRight} className="order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-emerald/10 flex items-center justify-center">
                  <span className="text-emerald/70 text-xs font-mono">04</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-emerald/20 to-transparent" />
              </div>
              <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-6">
                Umsetzung
              </h2>
              <div className="space-y-5 text-base text-white/50 leading-[1.8]">
                <p>
                  Für spezielle Themen arbeite ich mit ausgewählten Partnern zusammen, 
                  die einzelne Bereiche fachlich abdecken.
                </p>
                <p>
                  Dabei bleibe ich Ihr zentraler Ansprechpartner 
                  und sorge dafür, dass alle Bausteine aufeinander abgestimmt sind.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE STATEMENT - Strong Single Highlight */}
      <section className="py-28 sm:py-40">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <motion.div 
            {...fadeIn}
            className="text-center"
          >
            <div className="inline-block">
              <div className="w-12 h-px bg-emerald/40 mx-auto mb-12" />
            </div>
            <p className="font-playfair text-2xl sm:text-3xl md:text-4xl text-white/90 leading-[1.4] tracking-tight">
              Diese Bausteine ersetzen keine Strategie –
            </p>
            <p className="font-playfair text-2xl sm:text-3xl md:text-4xl text-emerald/80 leading-[1.4] tracking-tight mt-4">
              sie sorgen dafür, dass sie im Alltag funktioniert.
            </p>
            <div className="inline-block">
              <div className="w-12 h-px bg-emerald/40 mx-auto mt-12" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-6">
            <p className="text-base sm:text-lg text-white/50 leading-[1.8]">
              Am Ende entscheidet nicht, was möglich ist.
            </p>
            <p className="text-base sm:text-lg text-white/50 leading-[1.8]">
              Sondern was tatsächlich umgesetzt wird.
            </p>
            <p className="font-playfair text-xl sm:text-2xl text-white/80 leading-relaxed pt-6">
              Und genau darin liegt der Unterschied zwischen einer guten Idee 
              und einer funktionierenden Strategie.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn}>
            <div className="border-t border-white/10 pt-16 sm:pt-20">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">
                Nächster Schritt
              </p>
              <h2 className="font-playfair text-3xl sm:text-4xl font-semibold text-white mb-5">
                Verstehen Sie Ihre Strategie
              </h2>
              <p className="text-base sm:text-lg text-white/50 mb-10 max-w-md">
                Lassen Sie uns gemeinsam herausfinden, wie Ihre finanziellen 
                Entscheidungen optimal aufeinander abgestimmt werden können.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/strategiegespraech')}
                  className="group px-6 py-3.5 bg-white text-[#0c1017] rounded-lg text-sm font-medium hover:bg-white/95 transition-colors flex items-center justify-center gap-2"
                  data-testid="zusammenarbeit-booking-btn"
                >
                  Strategiegespräch vereinbaren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default Zusammenarbeit;
